import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, ShieldCheck, KeyRound, ArrowRight, UserPlus, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAdmin } from '../../contexts/AdminContext';
import { useToast } from '../../hooks/use-toast';

/**
 * Admin sign-in flow (real backend + Resend OTP).
 * Steps:
 *   creds  → email + password (verified via /api/auth/login which then triggers an OTP)
 *   otp    → 6-digit code (verified via /api/auth/otp/verify, purpose=login)
 * First-time setup flow (no password yet):
 *   setup-email → email
 *   setup-otp   → code (purpose=setup) → returns reset_token
 *   setup-pw    → set new password + confirm → auto-signed in
 */
const AdminLogin = () => {
  const { startLogin, verifyOtp, startSetup, verifySetupOtp, setPassword } = useAdmin();
  const nav = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState('creds');
  const [flow, setFlow] = useState('signin'); // signin | first-time | forgot
  const [email, setEmail] = useState('');
  const [password, setPassword_] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  const submitCreds = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast({ title: 'Enter your email and password' });
    setBusy(true);
    const r = await startLogin(email, password);
    setBusy(false);
    if (!r.ok) return toast({ title: r.error });
    toast({ title: 'Verification code sent', description: `Check ${email} for a 6-digit code.` });
    setStep('otp');
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast({ title: 'Enter the full 6-digit code' });
    setBusy(true);
    const r = await verifyOtp(email, otp);
    setBusy(false);
    if (!r.ok) return toast({ title: r.error });
    nav('/admin');
  };

  const startFirstTime = async (e) => {
    e.preventDefault();
    if (!email) return toast({ title: 'Enter your admin email' });
    setBusy(true);
    const r = await startSetup(email);
    setBusy(false);
    if (!r.ok) return toast({ title: r.error });
    toast({ title: 'Setup code sent', description: `Check ${email} for your 6-digit code.` });
    setStep('setup-otp');
  };

  const submitSetupOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast({ title: 'Enter the full 6-digit code' });
    setBusy(true);
    const r = await verifySetupOtp(email, otp);
    setBusy(false);
    if (!r.ok) return toast({ title: r.error });
    setResetToken(r.resetToken);
    setStep('setup-pw');
  };

  const submitNewPw = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) return toast({ title: 'Password must be at least 8 characters' });
    if (newPw !== confirmPw) return toast({ title: 'Passwords do not match' });
    setBusy(true);
    const r = await setPassword(resetToken, newPw);
    setBusy(false);
    if (!r.ok) return toast({ title: r.error });
    toast({ title: 'Password set — welcome to the admin portal' });
    nav('/admin');
  };

  const back = (s) => () => { setOtp(''); setStep(s); };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F0532] via-[#1F0068] to-[#0F0532] p-5">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8 text-white">
          <ShieldCheck className="w-8 h-8 text-[#FF5C39]" />
          <div className="leading-tight"><div className="font-bold text-lg">Domestic Home Services</div><div className="text-[10px] tracking-[0.22em] text-white/60 uppercase">Admin Portal</div></div>
        </div>
        <div className="bg-white rounded-3xl p-6 md:p-10 shadow-2xl">
          {step === 'creds' && (
            <>
              <h1 className="text-2xl font-bold text-[#1F0068]">Administrator sign in</h1>
              <p className="text-sm text-[#1F0068]/60 mt-1">A one-time code will be sent to your email after your password is verified.</p>
              <form onSubmit={submitCreds} className="mt-6 space-y-4">
                <div>
                  <Label className="text-[#1F0068] font-semibold">Email</Label>
                  <div className="relative mt-2"><Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" /><Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="h-12 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" /></div>
                </div>
                <div>
                  <Label className="text-[#1F0068] font-semibold">Password</Label>
                  <div className="relative mt-2"><Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" /><Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword_(e.target.value)} className="h-12 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" /></div>
                </div>
                <Button data-testid="admin-continue" type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white font-semibold">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
              </form>
              <div className="mt-5 text-center space-y-2">
                <button data-testid="admin-forgot-password" type="button" onClick={() => { setFlow('forgot'); setStep('setup-email'); }} className="text-xs text-[#FF5C39] hover:text-[#e0492b] underline font-semibold block mx-auto">
                  Forgot your password? Reset via email →
                </button>
                <button data-testid="admin-first-time" type="button" onClick={() => { setFlow('first-time'); setStep('setup-email'); }} className="text-xs text-[#1F0068]/70 hover:text-[#1F0068] underline font-semibold block mx-auto">
                  First-time setup? Create your password →
                </button>
              </div>
            </>
          )}

          {step === 'otp' && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-[#FFEDD6] flex items-center justify-center text-[#1F0068]"><KeyRound className="w-5 h-5" /></div>
                <div><h1 className="text-2xl font-bold text-[#1F0068]">Enter your code</h1><p className="text-sm text-[#1F0068]/60">Sent to <b>{email}</b></p></div>
              </div>
              <form onSubmit={submitOtp}>
                <Input data-testid="admin-otp" value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))} maxLength={6} placeholder="6-digit code" className="h-14 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10 text-center text-2xl tracking-widest" />
                <Button data-testid="admin-verify-otp" type="submit" disabled={busy} className="mt-3 w-full h-12 rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white font-semibold">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ShieldCheck className="w-4 h-4 ml-1" /></>}
                </Button>
                <button type="button" onClick={back('creds')} className="mt-3 text-xs text-[#1F0068]/70 underline block mx-auto">Back</button>
              </form>
            </>
          )}

          {step === 'setup-email' && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-[#FFEDD6] flex items-center justify-center text-[#1F0068]"><UserPlus className="w-5 h-5" /></div>
                <div>
                  <h1 className="text-2xl font-bold text-[#1F0068]">{flow === 'forgot' ? 'Reset your password' : 'First-time setup'}</h1>
                  <p className="text-sm text-[#1F0068]/60">Enter your admin email — we’ll send you a verification code so you can {flow === 'forgot' ? 'set a new password' : 'create your password'}.</p>
                </div>
              </div>
              <form onSubmit={startFirstTime} className="space-y-3">
                <div className="relative"><Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" /><Input data-testid="admin-setup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your admin email" className="h-12 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" /></div>
                <Button data-testid="admin-setup-send" type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white font-semibold">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send code <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
                <button type="button" onClick={() => { setFlow('signin'); back('creds')(); }} className="mt-1 text-xs text-[#1F0068]/70 underline block mx-auto">Back to sign in</button>
              </form>
            </>
          )}

          {step === 'setup-otp' && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-[#FFEDD6] flex items-center justify-center text-[#1F0068]"><KeyRound className="w-5 h-5" /></div>
                <div><h1 className="text-2xl font-bold text-[#1F0068]">Enter your code</h1><p className="text-sm text-[#1F0068]/60">Sent to <b>{email}</b></p></div>
              </div>
              <form onSubmit={submitSetupOtp}>
                <Input data-testid="admin-setup-otp" value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))} maxLength={6} placeholder="6-digit code" className="h-14 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10 text-center text-2xl tracking-widest" />
                <Button data-testid="admin-setup-verify" type="submit" disabled={busy} className="mt-3 w-full h-12 rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white font-semibold">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
                <button type="button" onClick={back('setup-email')} className="mt-3 text-xs text-[#1F0068]/70 underline block mx-auto">Back</button>
              </form>
            </>
          )}

          {step === 'setup-pw' && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-[#FFEDD6] flex items-center justify-center text-[#1F0068]"><Lock className="w-5 h-5" /></div>
                <div><h1 className="text-2xl font-bold text-[#1F0068]">{flow === 'forgot' ? 'Set a new password' : 'Set your password'}</h1><p className="text-sm text-[#1F0068]/60">Use at least 8 characters, with a mix of letters and numbers.</p></div>
              </div>
              <form onSubmit={submitNewPw} className="space-y-3">
                <div className="relative"><Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" /><Input data-testid="admin-newpw" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password" className="h-12 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" /></div>
                <div className="relative"><Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" /><Input data-testid="admin-confirmpw" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Confirm password" className="h-12 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" /></div>
                <Button data-testid="admin-set-password" type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white font-semibold">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Set password & sign in <ShieldCheck className="w-4 h-4 ml-1" /></>}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
